import { useContext, useMemo } from 'react';
import { IntlShape, useIntl } from 'react-intl';

import { IntlContext } from '@/i18n';
import { getLocalizedPartnerType } from '@/i18n/getLocalizedPartnerType';
import { IPartner } from '@/modules/partner/model';
import { getLocalizedTaxProfile } from '@/modules/taxProfile/i18n/getLocalizedTaxProfile';
import { CountryCode, TCountryCodes } from '@/shared/models/country-code';
import { DescriptionList } from '@/shared/view/DescriptionList';

import { messages } from './companyMessages';
import s from './styles.module.css';
import { useCompanyInfo } from './us/PayoutsInfo';

interface Props {
  partner: IPartner;
  className: string;
  onEdit?: () => void;
}

const mapToCompanyInfo = (partner: IPartner, intl: IntlShape) => ({
  partnerType: partner.partnerType ? getLocalizedPartnerType(partner.partnerType, intl) : '',
  legalName: partner.legalName,
  taxProfile: getLocalizedTaxProfile(partner.taxProfile, intl),
  vatRegNo: partner.vatRegNo,
  companyRegNum: partner.companyRegNumber,
  companyInn: partner.inn,
  companyKpp: partner.legalKpp,
  address: partner.legalAddress1,
  taxRegNo: partner.taxRegNo,
  tradeLicenseNumber: partner.tradeLicenseNumber,
  ein: partner.ein,
  dbaName: partner.dbaName,
  licenseNumber: partner.licenseNumber,
});

type TTitleLine = [keyof ReturnType<typeof mapToCompanyInfo>, string];

const skipEmpty = (item: TTitleLine | false): item is TTitleLine => item !== false;

const CompanyInfo = ({ className, partner, onEdit }: Props) => {
  const intl = useIntl();
  const { region } = useContext(IntlContext);

  const usTitles = useCompanyInfo({ region, partner });

  const titles = useMemo((): Array<TTitleLine> => {
    const allTitles: Array<TTitleLine | false> = [
      region === CountryCode.Russia && [
        'partnerType',
        intl.formatMessage(messages.companyTypeTitle),
      ],
      ['legalName', intl.formatMessage(messages.companyNameTitle)],
      region === CountryCode.GreatBritain && [
        'taxProfile',
        intl.formatMessage(messages.taxProfileTitle),
      ],
      ([CountryCode.GreatBritain, CountryCode.France] as TCountryCodes[]).includes(region) && [
        'vatRegNo',
        intl.formatMessage(messages.vatRegNoTitle),
      ],
      region === CountryCode.GreatBritain && [
        'companyRegNum',
        intl.formatMessage(messages.regNumTitle),
      ],
      region === CountryCode.Russia && ['companyInn', intl.formatMessage(messages.innTitle)],
      region === CountryCode.Russia && ['companyKpp', intl.formatMessage(messages.kppTitle)],
      region === CountryCode.ArabEmirates && [
        'tradeLicenseNumber',
        intl.formatMessage(messages.tradeLicenseNumberTitle),
      ],
      region === CountryCode.ArabEmirates && [
        'taxRegNo',
        intl.formatMessage(messages.taxRegNoTitle),
      ],
      ['address', intl.formatMessage(messages.addressTitle)],
    ];

    if (usTitles.length) {
      allTitles.push(...usTitles);
    }

    return allTitles.filter(skipEmpty);
  }, [intl, region, usTitles]);

  const action =
    onEdit && region === CountryCode.GreatBritain ? (
      <span onClick={onEdit} className={s.editButton}>
        {intl.formatMessage(messages.editAction)}
      </span>
    ) : undefined;

  const values = useMemo(() => mapToCompanyInfo(partner, intl), [partner, intl]);

  return (
    <DescriptionList
      title={intl.formatMessage(messages.title)}
      className={className}
      values={values}
      titles={titles}
      action={action}
    />
  );
};

export { CompanyInfo };
